<template>
  <Teleport to="body">
    <transition name="rlb-fade">
      <div
        v-if="isOpen"
        class="rlb-backdrop"
        role="dialog"
        aria-modal="true"
        @click.self="close"
      >
        <button class="rlb-btn rlb-close" type="button" aria-label="Fermer" @click="close">
          <span v-html="ICON_CLOSE"></span>
        </button>

        <button
          v-if="resources.length > 1"
          class="rlb-btn rlb-nav rlb-prev"
          type="button"
          aria-label="Précédent"
          @click="prev"
        >
          <span v-html="ICON_PREV"></span>
        </button>

        <div class="rlb-content" @click.stop>
          <div class="rlb-stage">
            <ResourceMedia
              v-if="currentResource"
              :key="index"
              :resource="currentResource"
              :url="currentUrl"
              context="lightbox"
              fit="contain"
            />
          </div>

          <div v-if="caption" class="rlb-caption">
            <h3 v-if="cleanTitle" class="rlb-title">{{ cleanTitle }}</h3>
            <p v-if="cleanDescription" class="rlb-description">{{ cleanDescription }}</p>
          </div>
        </div>

        <button
          v-if="resources.length > 1"
          class="rlb-btn rlb-nav rlb-next"
          type="button"
          aria-label="Suivant"
          @click="next"
        >
          <span v-html="ICON_NEXT"></span>
        </button>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, defineProps, defineEmits, onBeforeUnmount, watch } from 'vue';
import ResourceMedia from './ResourceMedia.vue';
import { cleanText } from '../../utils/galleryText';

/*
 * ResourceLightbox — shared multi-format lightbox.
 *
 * API contract (chosen: `selectedIndex` as the single source of truth,
 * exposed through `v-model:selectedIndex`):
 *   - `null` (the default)      => closed.
 *   - a valid Number in range   => open at that resource.
 * Consumers just hold a `selectedIndex` ref and bind it:
 *     <ResourceLightbox
 *       :resources="resources"
 *       :urls="resourceUrls"
 *       v-model:selectedIndex="selectedIndex" />
 * Opening = set it to an index; closing and prev/next navigation are emitted
 * back via `update:selectedIndex` (so the parent stays in sync automatically).
 */

const props = defineProps({
  // Resources in the same order as `urls`. Each: { name, title, description }.
  resources: { type: Array, default: () => [] },
  // Resolved URLs aligned with `resources` ('' loading, null failed).
  urls: { type: Array, default: () => [] },
  // Currently open index, or null when closed.
  selectedIndex: { type: Number, default: null },
});

const emit = defineEmits(['update:selectedIndex']);

const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const ICON_PREV = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>';
const ICON_NEXT = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';

const index = computed(() => props.selectedIndex);
const isOpen = computed(
  () =>
    index.value !== null &&
    index.value !== undefined &&
    index.value >= 0 &&
    index.value < props.resources.length
);

const currentResource = computed(() => (isOpen.value ? props.resources[index.value] : null));
const currentUrl = computed(() => {
  if (!isOpen.value) return '';
  const value = props.urls[index.value];
  // Keep `null` (failed) distinct from `''` (loading); only map missing -> loading.
  return value === undefined ? '' : value;
});

const cleanTitle = computed(() => cleanText(currentResource.value?.title));
const cleanDescription = computed(() => cleanText(currentResource.value?.description));
const caption = computed(() => cleanTitle.value || cleanDescription.value);

const close = () => emit('update:selectedIndex', null);

const go = (step) => {
  const total = props.resources.length;
  if (!isOpen.value || total <= 1) return;
  emit('update:selectedIndex', (index.value + step + total) % total);
};
const prev = () => go(-1);
const next = () => go(1);

const onKeydown = (event) => {
  if (!isOpen.value) return;
  if (event.key === 'Escape') close();
  else if (event.key === 'ArrowLeft') prev();
  else if (event.key === 'ArrowRight') next();
};

// Lock body scroll + attach key listener only while open.
let savedOverflow = '';
watch(
  isOpen,
  (open, wasOpen) => {
    if (typeof document === 'undefined') return;
    if (open) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKeydown);
    } else if (wasOpen) {
      // Only undo what the open-branch actually did — on the immediate run of
      // a lightbox mounted closed (`wasOpen === undefined`) there is nothing
      // to restore, and resetting body overflow could stomp outer state.
      document.body.style.overflow = savedOverflow;
      window.removeEventListener('keydown', onKeydown);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return;
  // Restore the scroll lock only if we are the ones holding it.
  if (isOpen.value) document.body.style.overflow = savedOverflow;
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.rlb-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(8, 8, 8, 0.94);
  color: #fff;
}

.rlb-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  max-width: 100vw;
}

.rlb-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(1200px, 92vw);
  max-width: 100%;
  height: 80vh;
  max-height: 80vh;
}

.rlb-stage > * {
  max-width: 100%;
  max-height: 100%;
}

.rlb-caption {
  max-width: min(900px, 92vw);
  text-align: center;
}

.rlb-title {
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.3;
}

.rlb-description {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.45;
  opacity: 0.82;
}

.rlb-btn {
  position: absolute;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease;
}

.rlb-btn:hover {
  background: rgba(255, 255, 255, 0.24);
}

.rlb-btn:active {
  transform: scale(0.94);
}

.rlb-close {
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  font-size: 22px;
}

.rlb-nav {
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  font-size: 26px;
}

.rlb-nav:active {
  transform: translateY(-50%) scale(0.94);
}

.rlb-prev {
  left: 16px;
}

.rlb-next {
  right: 16px;
}

.rlb-fade-enter-active,
.rlb-fade-leave-active {
  transition: opacity 200ms ease;
}

.rlb-fade-enter-from,
.rlb-fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .rlb-backdrop {
    padding: 12px;
  }

  .rlb-stage {
    width: 100%;
    height: 70vh;
  }

  .rlb-nav {
    width: 42px;
    height: 42px;
    font-size: 20px;
  }

  .rlb-prev {
    left: 6px;
  }

  .rlb-next {
    right: 6px;
  }
}
</style>
